import React, { FC, useMemo } from 'react'
import { Box, Link, Paper, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { DataLoad, TableToolbar } from 'src/components/ui'
import { useSensorsBillingStatsRequest } from 'src/hooks/api'
import { useQueryParams } from 'src/hooks/ui'
import { billingStatsFilterResetKeys, BillingStatsGridRow, buildBillingStatsGridRows, formatStatWithStaging } from './billingStatsRows'

// Plain numeric export value for the CSV toolbar; the grid cell itself is rendered with the staging suffix.
const formatStatValue = ({ value }: { value?: number | null }) => (value === null || value === undefined ? '-' : value)

const buildColumns = (onTitleClick: (row: BillingStatsGridRow) => void): GridColDef<BillingStatsGridRow>[] => [
  {
    field: 'title',
    headerName: 'Metric',
    flex: 1.5,
    minWidth: 260,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => onTitleClick(row)}
          sx={{ whiteSpace: 'normal', textAlign: 'left', cursor: 'pointer' }}
        >
          {row.title}
        </Link>
        <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{`ThingSpace: ${row.thingspaceHint}\nCLIMIT: ${row.climitHint}`}</Box>}>
          <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.6, cursor: 'help', flexShrink: 0 }} />
        </Tooltip>
      </Box>
    )
  },
  {
    field: 'thingspace',
    headerName: 'ThingSpace',
    flex: 1,
    minWidth: 120,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: formatStatValue,
    renderCell: ({ row }) => formatStatWithStaging(row.thingspace, row.thingspaceStaging)
  },
  {
    field: 'climit',
    headerName: 'CLIMIT',
    flex: 1,
    minWidth: 120,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: formatStatValue,
    renderCell: ({ row }) => formatStatWithStaging(row.climit, row.climitStaging)
  }
]

interface SensorBillingStatsAnalyticsProps {
  onStatSelect?: () => void
}

export const SensorBillingStatsAnalytics: FC<SensorBillingStatsAnalyticsProps> = ({ onStatSelect }) => {
  const { data, error, isInitialLoading, isError } = useSensorsBillingStatsRequest()
  const [, setQueryParams] = useQueryParams()
  const rows = buildBillingStatsGridRows(data?.thingspace, data?.climit)

  const columns = useMemo(
    () =>
      buildColumns(async (row) => {
        await setQueryParams(row.filters, billingStatsFilterResetKeys)
        onStatSelect?.()
      }),
    [setQueryParams, onStatSelect]
  )

  return (
    <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
      <Paper>
        <DataGridPremium<BillingStatsGridRow>
          autoHeight
          pagination={false}
          hideFooterPagination
          disableRowSelectionOnClick
          rows={rows}
          columns={columns}
          rowCount={rows.length}
          getRowId={(row) => row.key}
          getRowHeight={() => 'auto'}
          rowThreshold={2}
          columnThreshold={2}
          slots={{ toolbar: () => <TableToolbar exportedFileName={`${document.title}_billing_stats`} /> }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { py: 1 },
            '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: 'rgba(0, 0, 0, 0.6)',
              overflow: 'visible'
            }
          }}
        />
      </Paper>
    </DataLoad>
  )
}
